import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import errorBoundary from '@ifeng-fe/errorBoundary';
import { formatPeopleCount } from '@src/public/utils/format';
import { userIsLogin, userLogin } from '@src/public/utils/app';
import { getCommentVote, setCommentVote } from '@src/public/utils/interactionStorage';
import styles from './index.css';
import { Store } from '../../store';
import { postLikeComment } from '../../services';
import { POSITION_MAIN, POSITION_SUB, POSITION_SUB_TOP } from '../../constants';

const cx = classNames.bind(styles);

const isVoteSuccess = res => {
    if (res == null) {
        return false;
    }

    const code = res.code ?? res.data?.code;

    // vote.php 成功常见 code=1；部分场景直接返回可解析对象
    return code === 1 || code === '1' || code === 0 || code === '0' || code === undefined;
};

const Like = ({ commentId, like, count, subjectId, position }) => {
    const store = Store.useContainer();
    const { states, changeStates } = store;
    const { likeMessage, docMeta } = states;
    const localVote = getCommentVote(commentId);

    const [likeState, setLikeState] = useState(() => (localVote ? !!localVote.liked : !!like));
    const [countState, setCountState] = useState(count);
    const [hasVoted, setHasVoted] = useState(() => !!(localVote?.voted || localVote?.liked || like));
    const [lock, setLock] = useState(false);

    const persistVote = (liked, voted = true) => {
        setCommentVote(commentId, { liked, voted });
    };

    const syncLikeMessage = (newLike, newCount, voted) => {
        if (position === POSITION_SUB || position === POSITION_SUB_TOP) {
            changeStates({
                likeMessage: { id: commentId, like: newLike, count: newCount, hasVoted: voted },
            });
        }
    };

    const toggleLocalLike = () => {
        const newLike = !likeState;
        const newCount = newLike ? countState + 1 : Math.max(countState - 1, 0);

        setLikeState(newLike);
        setCountState(newCount);
        setHasVoted(true);
        persistVote(newLike, true);
        syncLikeMessage(newLike, newCount, true);
    };

    const handleClick = () => {
        if (!userIsLogin()) {
            userLogin();

            return;
        }

        if (lock) {
            return;
        }

        // 已请求过点赞接口：之后只切换选中态，不再发请求
        if (hasVoted) {
            toggleLocalLike();

            return;
        }

        // 首次点赞：请求接口，成功后点赞数 +1、icon 标红
        setLock(true);

        postLikeComment({
            commentId,
            subjectId,
            docUrl: docMeta?.docUrl || (subjectId ? `ucms_${subjectId}` : ''),
        })
            .then(res => {
                if (!isVoteSuccess(res)) {
                    return;
                }

                const newCount = countState + 1;

                setHasVoted(true);
                setLikeState(true);
                setCountState(newCount);
                persistVote(true, true);
                syncLikeMessage(true, newCount, true);
            })
            .catch(e => {
                console.error(e);
            })
            .finally(() => {
                setLock(false);
            });
    };

    useEffect(
        () => {
            const { id, like, count, hasVoted: voted } = likeMessage;

            if (id === commentId && position === POSITION_MAIN) {
                setLikeState(like);
                setCountState(count);
                if (voted) {
                    setHasVoted(true);
                }
                persistVote(!!like, !!(voted || like));
                changeStates({ likeMessage: {} });
            }
        },
        [likeMessage],
    );

    return (
        <div className={cx('like', { liked: likeState })} onClick={handleClick}>
            <div className={styles.likeCount}>{countState ? formatPeopleCount(countState) : ''}</div>
            <div className={styles.likeIcon} />
        </div>
    );
};

Like.propTypes = {
    commentId: PropTypes.string,
    like: PropTypes.bool,
    count: PropTypes.number,
    subjectId: PropTypes.string,
    position: PropTypes.string,
};

export default errorBoundary(Like);
